/**
 * Haptic feedback helpers (ADR-009 §4).
 *
 * Wraps `navigator.vibrate` so callers can fire a tap pattern
 * without re-checking feature support every time. Three call
 * patterns mirror the global mobile-app convention:
 *
 *   `lightTap()`   — quick 10 ms pulse for confirmed UI taps
 *                    (action-bar fold / check / call / etc.).
 *   `mediumTap()`  — 20 ms pulse for positive lifecycle moments
 *                    (caller wins a settled hand).
 *   `heavyTap()`   — `[0, 30, 50, 30]` triple-pulse for hard
 *                    rejects (action submit error / rebuy
 *                    insufficient balance / etc.).
 *
 * Feature detect short-circuit: `'vibrate' in navigator` returns
 * `false` on desktop browsers, iOS Safari (which never shipped
 * the API), and any SSR / Node test environment. The helpers
 * silently no-op there — callers don't need to guard.
 *
 * Browser support note: Chrome / Edge / Firefox / Samsung
 * Internet on Android implement `navigator.vibrate`. iOS Safari
 * does NOT — the haptic surface degrades gracefully to a
 * visual-only signal on iOS. Default-on; a user-settings opt-out
 * is a future enhancement.
 */

export const HAPTIC_LIGHT_MS = 10;
export const HAPTIC_MEDIUM_MS = 20;
export const HAPTIC_HEAVY_PATTERN: ReadonlyArray<number> = [0, 30, 50, 30];

/**
 * Fire `navigator.vibrate(pattern)` when the API is available.
 * Returns `true` on a successful trigger, `false` on the
 * feature-detect short-circuit (so a test harness can assert
 * the silent-skip without a thrown error).
 */
export function vibrate(pattern: number | ReadonlyArray<number>): boolean {
  if (typeof navigator === "undefined") return false;
  if (!("vibrate" in navigator)) return false;
  try {
    // `vibrate` accepts `number | number[]`. The readonly cast
    // on `ReadonlyArray<number>` keeps the lib type happy
    // without a mutable copy — the API doesn't mutate its
    // argument.
    return (navigator as Navigator).vibrate(pattern as number | number[]);
  } catch {
    // Some embedded browsers throw on a long pattern; the
    // silent-fail keeps the caller's tap path lossy-but-alive.
    return false;
  }
}

export function lightTap(): boolean {
  return vibrate(HAPTIC_LIGHT_MS);
}

export function mediumTap(): boolean {
  return vibrate(HAPTIC_MEDIUM_MS);
}

export function heavyTap(): boolean {
  return vibrate(HAPTIC_HEAVY_PATTERN);
}
